import { useState } from 'react';
import { useQuery } from '../../hooks/useQuery';
import { api, type PlanVersion, type PlanRevenueContractRow, type PlanRevenueType } from '../../lib/api';
import { Spinner, ActionButton, Modal, Badge } from '../ui';
import { SaveStatus, useSaveState } from '../SaveStatus';

/**
 * Planning v2 Revenue — Fixed-Fee / Subscription contracts in the grid idiom:
 * a row grid with a centered popup editor and an import-from-engagements action.
 * Recognised amounts flow into the Budget Grid (rev.ff / rev.sub) on recompute.
 */

const money = (n: number | null | undefined) => (n == null ? '' : n.toLocaleString(undefined, { maximumFractionDigits: 0 }));
const box: React.CSSProperties = { fontSize: 12, padding: '4px 7px', border: '0.5px solid var(--color-border-strong)', borderRadius: 'var(--radius-sm)', background: 'var(--color-surface)', color: 'var(--color-text)' };
const th: React.CSSProperties = { padding: '4px 8px', fontSize: 11, fontWeight: 600, textAlign: 'left', borderBottom: '0.5px solid var(--color-border)', color: 'var(--color-text-muted)' };
const td: React.CSSProperties = { padding: '3px 8px', fontSize: 11, borderBottom: '0.5px solid var(--color-border)' };
const row: React.CSSProperties = { display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 };
const lab: React.CSSProperties = { fontSize: 11, color: 'var(--color-text-muted)', width: 110 };

export default function PlanContractsGrid({ plan, canWrite }: { plan: PlanVersion; canWrite: boolean }) {
  const planId = plan.id;
  const cQ = useQuery(() => api.fpa.plans.contracts(planId), [planId]);
  const deptsQ = useQuery(() => api.fpa.departments(), [planId]);
  const save = useSaveState();
  const [edit, setEdit] = useState<PlanRevenueContractRow | 'new' | null>(null);

  if (cQ.isLoading) return <Spinner />;
  const contracts = cQ.data?.contracts ?? [];
  const departments = deptsQ.data?.departments ?? [];
  const deptName = (id: string) => departments.find((d) => d.id === id)?.name ?? id;

  // Each of these endpoints recomputes the plan in the same transaction as
  // its write, so there is no follow-up recompute call to make.
  async function withSave(fn: () => Promise<unknown>, note?: string) {
    if (await save.run(fn, note)) cQ.refetch();
  }

  return (
    <div style={{ borderBottom: '0.5px solid var(--color-border)' }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '8px 12px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, fontWeight: 500 }}>Contracts (Fixed-Fee / Subscription)</span>
        {canWrite && <>
          <ActionButton onClick={() => setEdit('new')}>+ Add contract</ActionButton>
          <ActionButton onClick={() => withSave(() => api.fpa.plans.importContracts(planId), 'imported from engagements')} disabled={save.busy}>Import from engagements</ActionButton>
        </>}
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <SaveStatus state={save} />
        </span>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', minWidth: '100%' }}>
          <thead><tr><th style={th}>Label</th><th style={th}>Type</th><th style={th}>Dept</th><th style={{ ...th, textAlign: 'right' }}>Value</th><th style={th}>Start</th><th style={{ ...th, textAlign: 'right' }}>Months</th><th style={th}>Notes</th></tr></thead>
          <tbody>
            {contracts.map((c) => (
              <tr key={c.id} style={{ cursor: canWrite ? 'pointer' : 'default' }} onClick={() => canWrite && setEdit(c)}>
                <td style={td}>{c.label}</td>
                <td style={td}><Badge variant={c.revenue_type === 'FF' ? 'blue' : 'green'}>{c.revenue_type}</Badge></td>
                <td style={td}>{deptName(c.department_id)}</td>
                <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{money(c.value)}</td>
                <td style={td}>{c.start_month}</td>
                <td style={{ ...td, textAlign: 'right' }}>{c.duration_months}</td>
                <td style={{ ...td, color: 'var(--color-text-muted)' }}>{c.notes}</td>
              </tr>
            ))}
            {contracts.length === 0 && <tr><td style={{ ...td, color: 'var(--color-text-muted)' }} colSpan={7}>No contracts — add one or import from engagements.</td></tr>}
          </tbody>
        </table>
      </div>
      {edit && <ContractEditor planId={planId} row={edit === 'new' ? undefined : edit} departments={departments}
        onClose={() => setEdit(null)} onSaved={() => { setEdit(null); save.markSaved('contract saved'); cQ.refetch(); }}
        onDelete={edit !== 'new' ? () => { const id = edit.id; setEdit(null); withSave(() => api.fpa.plans.deleteContract(planId, id), 'contract deleted'); } : undefined} />}
    </div>
  );
}

function ContractEditor({ planId, row: r, departments, onClose, onSaved, onDelete }: {
  planId: string; row?: PlanRevenueContractRow; departments: Array<{ id: string; name: string }>;
  onClose: () => void; onSaved: () => void; onDelete?: () => void;
}) {
  const [f, setF] = useState({
    label: r?.label ?? '', revenue_type: r?.revenue_type ?? 'FF' as PlanRevenueType,
    department_id: r?.department_id ?? (departments[0]?.id ?? ''), value: r ? String(r.value) : '',
    start_month: r?.start_month ?? '', duration_months: r ? String(r.duration_months) : '12', notes: r?.notes ?? '',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const set = (k: keyof typeof f, v: unknown) => setF((p) => ({ ...p, [k]: v }));

  async function save() {
    if (!f.label.trim() || !f.department_id || !/^\d{4}-\d{2}$/.test(f.start_month)) { setErr('Label, department and start month (YYYY-MM) are required'); return; }
    setBusy(true); setErr(null);
    try {
      await api.fpa.plans.upsertContract(planId, {
        ...(r ? { id: r.id } : {}),
        label: f.label.trim(), revenue_type: f.revenue_type, department_id: f.department_id,
        value: Number(f.value || 0), start_month: f.start_month, duration_months: Math.max(1, Number(f.duration_months || 1)), notes: f.notes || null,
      });
      onSaved();
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  }

  return (
    <Modal title={r ? 'Edit contract' : 'Add contract'} onClose={onClose} width={500}>
      <div style={{ padding: 20 }}>
        {err && <p style={{ color: 'var(--color-danger, #c0392b)', fontSize: 12 }}>{err}</p>}
        <div style={row}><span style={lab}>Label</span><input autoFocus value={f.label} onChange={(e) => set('label', e.target.value)} style={{ ...box, flex: 1 }} /></div>
        <div style={row}><span style={lab}>Type</span><select value={f.revenue_type} onChange={(e) => set('revenue_type', e.target.value)} style={box}><option value="FF">Fixed-Fee</option><option value="SUB">Subscription</option></select></div>
        <div style={row}><span style={lab}>Department</span><select value={f.department_id} onChange={(e) => set('department_id', e.target.value)} style={{ ...box, flex: 1 }}>{departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}</select></div>
        <div style={row}><span style={lab}>Value</span><input value={f.value} onChange={(e) => set('value', e.target.value)} style={{ ...box, width: 140 }} /></div>
        <div style={row}><span style={lab}>Start / months</span><input value={f.start_month} onChange={(e) => set('start_month', e.target.value)} placeholder="YYYY-MM" style={{ ...box, width: 110 }} /><input value={f.duration_months} onChange={(e) => set('duration_months', e.target.value)} style={{ ...box, width: 70 }} /><span style={{ fontSize: 10, color: 'var(--color-text-muted)' }}>spread evenly over the term</span></div>
        <div style={row}><span style={lab}>Notes</span><input value={f.notes} onChange={(e) => set('notes', e.target.value)} style={{ ...box, flex: 1 }} /></div>
        <div style={{ display: 'flex', gap: 8, marginTop: 16, alignItems: 'center' }}>
          <ActionButton variant="primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : r ? 'Save' : 'Add'}</ActionButton>
          <ActionButton onClick={onClose}>Cancel</ActionButton>
          {onDelete && <span style={{ marginLeft: 'auto' }}><ActionButton variant="danger" onClick={onDelete}>Delete</ActionButton></span>}
        </div>
      </div>
    </Modal>
  );
}
